import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled, { css } from "styled-components";
import { faCircleNotch } from "@fortawesome/pro-solid-svg-icons";
import { Link } from "react-router-dom";
import { NavContext } from "./context/NavContext";
import {
  BORDER_WIDTH,
  CALL_TO_ACTION_MAX_WIDTH,
  RAIL_EDGE_FADE_WIDTH,
  CELL_HEIGHT,
  GENERIC_ITEM_BORDER_RADIUS,
  SECTION_HEADING_FONT_SIZE,
  SURFACE_CARD_RADIUS,
  THUMBNAIL_BORDER_RADIUS,
  UNIT_TILE_MIN_WIDTH,
} from "./constants";
import { Container } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { SPACE_LG, SPACE_MD, SPACE_SM, SPACE_XL, SPACE_XS } from "./spacing";
import { elevation1Clipped } from "./elevation";
import { LessonImage } from "./LessonImage";
import {
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_PRIMARY_HOVER,
  COLOR_TEXT_TERTIARY,
  COLOR_GRAY_BORDER,
  COLOR_GRAY_4F,
  COLOR_GRAY_INACTIVE,
  COLOR_RED_BRIGHT,
  COLOR_LINK,
  COLOR_WHITE,
  COLOR_BLACK,
  COLOR_NEUTRAL_BORDER,
  COLOR_NEUTRAL_PROGRESS,
  COLOR_SURFACE_CONTAINER_STRONG,
  COLOR_PLAY_BUTTON,
} from "./colors";

export const enum ZIndex {
  BackgroundImage = -1,
  Default = 0,
  DisabledOverlay = 1,
  ListeningAnimation = 1,
  Transcription = 1,
  VideoUploadOverlay = 1,
  SelectGotoButton = 2,
  ModalOverlay = 3,
  UserInfoPanel = 5,
  LessonDiffModalContent = 10,
  LessonSettingsModalBody = 10,
  ActionMenu = 10,
  CompletionMenu = 11,
  ErrorModal = 15,
  PopoverImage = 2000,
}

export const SearchInput = styled.input`
  border-radius: 18px;
  height: 33px;
  width: 100%;
  padding: 5px 5px 5px 20px;
  border: 1px solid ${COLOR_GRAY_BORDER};
`;
export const ModalTitle = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 15px;
  padding-left: 20px;
  margin: 0;
`;

export const ModalBody = styled.div`
  border-bottom: 1px solid ${COLOR_GRAY_BORDER};
  border-top: 1px solid ${COLOR_GRAY_BORDER};
  display: flex;
  flex-direction: column;
  min-height: 400px;
  flex: 1 1 100%;
`;
export const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 20px;
  padding-top: 10px;
  padding-bottom: 10px;
  margin: 0;
  flex-shrink: 0;
`;
export const FlexRow = styled.div`
  display: flex;
  align-items: center;
`;

export const ProgressBar = styled.div`
  height: 3px;
  display: flex;
  border-radius: 3px;
  background-color: ${COLOR_NEUTRAL_BORDER};
`;

export const ProgressBarFill = styled.div<{ width: number }>`
  width: ${({ width }) => width}%;
  height: 100%;
  border-radius: 12px;
  background-color: ${COLOR_NEUTRAL_PROGRESS};
`;

export const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SchoolLoginButton = styled.button`
  margin: 10px auto 10px auto;
  display: block;
  position: relative;
  background-color: ${COLOR_WHITE};
  border: 1px solid ${COLOR_TEXT_PRIMARY};
  border-radius: 8px;
  min-height: 48px;
  width: 300px;
  margin: 10px auto;
  text-align: center;
`;

export const SchoolLoginButtonIconImage = styled.img`
  position: absolute;
  height: 28px;
  width: 28px;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
`;

export const SECTION_SPACING = SPACE_MD;

export const Section = styled.div`
  padding: ${SECTION_SPACING}px 0;
`;

export const SectionHeading = styled.h2`
  padding: ${SECTION_SPACING}px 0;
  margin-bottom: 0;
  font-size: ${SECTION_HEADING_FONT_SIZE}px;
  font-weight: 700;
`;

export const UnitTilesRow = styled.div<{ minWidth?: number }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${UNIT_TILE_MIN_WIDTH}px, 1fr)
  );
  row-gap: ${SPACE_LG}px;
  column-gap: ${SPACE_XL}px;
  padding: ${SECTION_SPACING}px 0;

  & > * {
    min-width: 0;
  }
`;

const CELL_META_FONT_SIZE = 10;

export const CELL_COVER_SIZE = 94;

export const CELL_COVER_SIZE_COMPACT = 64;

export const clampLines = (lines: number) => css`
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  line-clamp: ${lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SingleLineText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CapsuleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACE_SM}px;
  width: 100%;
  padding: ${SPACE_MD}px ${SPACE_XL}px;
  border: none;
  border-radius: 100px;
  background-color: ${COLOR_SURFACE_CONTAINER_STRONG};
  font-size: 16px;
  color: ${COLOR_TEXT_PRIMARY};
`;

export const CallToActionButton = styled(CapsuleButton)`
  max-width: ${CALL_TO_ACTION_MAX_WIDTH}px;
  margin-left: auto;
  margin-right: auto;
  font-size: 17px;
  font-weight: 600;
`;

export const PlayButton = styled(CallToActionButton)`
  background-color: ${COLOR_PLAY_BUTTON};
  color: ${COLOR_WHITE};
`;

export const DetailsTitle = styled.h1`
  margin: 0;
  color: ${COLOR_TEXT_PRIMARY};
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 600;
  text-align: center;
`;

export const SectionLabel = styled.div`
  color: ${COLOR_TEXT_PRIMARY};
  font-size: 16px;
  font-weight: 600;
`;

export const FadedRailFrame = styled.div`
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: ${RAIL_EDGE_FADE_WIDTH}px;
    background: linear-gradient(to left, ${COLOR_WHITE}, transparent);
    pointer-events: none;
  }
`;

export const DragScrollRail = styled.div`
  overflow-x: auto;
  scrollbar-width: none;
  cursor: grab;
  user-select: none;

  &.is-dragging {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const fluidCover = css`
  width: 100%;
  aspect-ratio: 1 / 1;
`;

const fixedCover = css<{ $size?: number }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  flex-shrink: 0;
`;

const CoverBlock = styled.div<{ $size?: number; $isElevated: boolean }>`
  position: relative;
  border-radius: ${SURFACE_CARD_RADIUS}px;
  overflow: hidden;
  ${(p) => (p.$size === undefined ? fluidCover : fixedCover)}
  ${(p) => p.$isElevated && elevation1Clipped}
`;

export const CellCover = ({
  url,
  alt,
  size,
  isElevated = false,
  lazy = true,
  className,
}: {
  url: string | null;
  alt: string;
  size?: number;
  isElevated?: boolean;
  lazy?: boolean;
  className?: string;
}) => (
  <CoverBlock $size={size} $isElevated={isElevated} className={className}>
    {url && <LessonImage lazy={lazy} thumbSize="large" url={url} alt={alt} />}
  </CoverBlock>
);

export const CellMetaRow = styled.div<{ $gap: number }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.$gap}px;
  color: ${COLOR_TEXT_TERTIARY};
  font-size: ${CELL_META_FONT_SIZE}px;
`;

const CELL_META_ICON = {
  duration: `${process.env.PUBLIC_URL}/clock.svg`,
  ageRange: `${process.env.PUBLIC_URL}/child-reaching.svg`,
  lessonCount: `${process.env.PUBLIC_URL}/rectangle-history.svg`,
};

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE_XS}px;
`;

const MetaIcon = styled.img`
  display: block;
`;

export const CellMetaItem = ({
  icon,
  children,
}: {
  icon?: keyof typeof CELL_META_ICON;
  children: React.ReactNode;
}) => (
  <MetaItem>
    {icon && <MetaIcon src={CELL_META_ICON[icon]} alt="" />}
    {children}
  </MetaItem>
);

// Use this as a base for components that take up the full content view
export const PageContainer = styled(Container)`
  margin-top: 12px;
`;

export const LinkButton = styled.button`
  border: none;
  padding: 0;
  color: ${COLOR_LINK};
  background: transparent;
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }
`;

const themedLinkStyles = css`
  color: ${COLOR_TEXT_PRIMARY};
  text-decoration: underline;

  :hover {
    color: ${COLOR_BLACK};
    text-decoration: underline;
    text-decoration-thickness: 2px;
  }
`;

export const AppAnchor = styled.a.withConfig({
  shouldForwardProp: (prop) => prop !== "themed",
})<{ themed?: boolean }>`
  ${({ themed }) => themed && themedLinkStyles}
`;

export const AppLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== "themed",
})<{ themed?: boolean }>`
  ${({ themed }) => themed && themedLinkStyles}
`;

export const ErrorText = styled.div`
  color: ${COLOR_RED_BRIGHT};
  margin: 10px 0;
`;

export const TextInput = styled.input`
  border: 1px solid ${COLOR_GRAY_BORDER};
  border-radius: 4px;
  padding: 10px;
  font-size: 14px;
  flex: 1 1 100%;
`;

export const SecondaryButton = styled.button`
  border: 1px solid ${COLOR_GRAY_4F};
  background: none;
  cursor: pointer;
  color: ${COLOR_GRAY_4F};
  padding: 10px;
  border-radius: 10px;
  font-weight: 400;

  & > svg {
    /* Add margin to leading icons. */
    margin-right: 10px;
  }

  :hover {
    background-color: ${COLOR_GRAY_BORDER};
    color: ${COLOR_TEXT_PRIMARY};
    border-color: ${COLOR_TEXT_PRIMARY};
  }
`;

export const PrimaryButton = styled(SecondaryButton)`
  background-color: ${COLOR_TEXT_PRIMARY};
  border: 1px solid ${COLOR_TEXT_PRIMARY};
  color: ${COLOR_WHITE};

  :hover {
    background-color: ${COLOR_TEXT_PRIMARY_HOVER};
    border: 1px solid ${COLOR_TEXT_PRIMARY};
    color: ${COLOR_WHITE};
  }

  :disabled {
    background-color: ${COLOR_GRAY_BORDER};
    border: 1px solid ${COLOR_GRAY_BORDER};
    cursor: auto;
  }
`;
export const CtaButton = styled(PrimaryButton)`
  background-color: ${COLOR_TEXT_PRIMARY};
  border: 1px solid ${COLOR_TEXT_PRIMARY};
  color: ${COLOR_WHITE};
  border-radius: 10px;
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 400;
`;

export const SecondaryCtaButton = styled(PrimaryButton)`
  border: 1px solid ${COLOR_GRAY_4F};
  background: none;
  color: ${COLOR_GRAY_4F};
  border-radius: 10px;
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 400;
  :hover {
    background-color: ${COLOR_GRAY_BORDER};
    color: ${COLOR_TEXT_PRIMARY};
    border-color: ${COLOR_TEXT_PRIMARY};
  }
`;

export const AppFlowActionButton = styled.button<{ isEnabled: boolean }>`
  background-color: ${(p) =>
    p.isEnabled ? COLOR_TEXT_PRIMARY : COLOR_GRAY_INACTIVE};
  border: none;
  border-radius: 8px;
  color: ${COLOR_WHITE};
  cursor: ${(p) => (p.isEnabled ? "pointer" : "not-allowed")};
  padding: 8px 20px;
  width: 100%;
  font-size: 20px;
  font-weight: 400;
  margin: 20px 0;
`;

export const AppFlowLoginButton = styled.button<{ isEnabled?: boolean }>`
  background-color: ${(p) =>
    p.isEnabled === false ? COLOR_GRAY_INACTIVE : COLOR_TEXT_PRIMARY};
  border: none;
  border-radius: 8px;
  color: ${COLOR_WHITE};
  cursor: ${(p) => (p.isEnabled === false ? "not-allowed" : "pointer")};
  padding: 8px 20px;
  font-weight: 400;
  min-height: 48px;
  width: 300px;
  margin: 20px 0;
`;

export const GenericListItemBody = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  overflow: hidden;
  height: ${CELL_HEIGHT}px;
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  background-color: ${COLOR_WHITE};

  & div {
    text-decoration: none;
  }
`;

export const GenericListImageBlock = styled.div`
  height: 100%;
  width: ${CELL_HEIGHT - 2 * BORDER_WIDTH}px;
  display: flex;
  justify-content: center;
  overflow: hidden;
  padding: 10px;
  & img {
    border-radius: ${THUMBNAIL_BORDER_RADIUS}px;
  }
`;

export const Label = styled.div`
  font-size: 14px;
  font-weight: 400;
`;

export const BackNav: React.FC<{ label: string; callback?: () => void }> = ({
  label,
  callback,
}) => {
  const { goBack, hasBack } = React.useContext(NavContext);
  return !!callback || hasBack() ? (
    <Link
      to=""
      onClick={(event) => {
        if (!!callback) {
          callback();
        } else {
          goBack();
        }
        event.preventDefault();
      }}
      style={{
        textDecoration: "none",
        fontSize: "20px",
      }}
    >
      ‹<span className="d-none d-md-inline-block mx-2">{label}</span>
    </Link>
  ) : (
    <></>
  );
};

export const BackNavRow: React.FC<{
  label: string | null;
  callback?: () => void;
  children?: React.ReactNode;
  rightChildren?: React.ReactNode;
}> = ({ label, callback, children, rightChildren }) => {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr" }}>
      <div
        style={{
          whiteSpace: "nowrap",
        }}
      >
        {label !== null && <BackNav label={label} callback={callback} />}
      </div>
      <div>{children}</div>
      <div className="text-end" style={{ alignContent: "center" }}>
        {rightChildren}
      </div>
    </div>
  );
};

export const LoadingIndicator = (props: { size?: "1x" | "2x" | "4x" }) => {
  return <FontAwesomeIcon icon={faCircleNotch} spin={true} size={props.size} />;
};

const EMPTY_RESULTS_IMAGE_WIDTH = 180;

const EmptyResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE_LG}px;
  padding: ${SPACE_XL}px 0;

  & > img {
    width: ${EMPTY_RESULTS_IMAGE_WIDTH}px;
    max-width: 100%;
  }
`;

export const EmptyResults = (props: { message: string }) => {
  return (
    <EmptyResultsContainer>
      <img alt="" src={`${process.env.PUBLIC_URL}/girl-device-happy.png`} />
      <span>{props.message}</span>
    </EmptyResultsContainer>
  );
};

export const AppFlowStepContainer: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
  extraClassName?: string;
}> = ({ children, style, extraClassName }) => {
  return (
    <div
      className={"d-flex flex-column align-items-center p-3 " + extraClassName}
      style={{ minHeight: "500px", ...style }}
    >
      {children}
    </div>
  );
};

export const AppFlowSupportingText: React.FC<{
  text: string | React.ReactNode;
  style?: React.CSSProperties;
}> = ({ text, style }) => {
  return (
    <span
      style={{
        fontSize: "clamp(12px, 3vw, 16px)",
        lineHeight: "22px",
        ...style,
      }}
    >
      {text}
    </span>
  );
};

export const AppFlowTitle: React.FC<{
  title: string;
  subtitle?: string | React.ReactNode;
  style?: React.CSSProperties;
}> = ({ title, subtitle, style }) => {
  return (
    <div className="d-flex flex-column align-items-center" style={{ ...style }}>
      <h2>{title}</h2>
      {subtitle && <AppFlowSupportingText text={subtitle} />}
    </div>
  );
};

export const AppFlowStepContentContainer: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
  extraClassName?: string;
}> = ({ children, style, extraClassName }) => {
  return (
    <div
      className={
        "row py-2 d-flex gap-2 flex-column w-100 align-items-center flex-grow-1 " +
        extraClassName
      }
      style={{ ...style }}
    >
      {children}
    </div>
  );
};

const ContinueButton = styled(PrimaryButton)`
  border-radius: 8px;
  padding: 0.5rem 3rem;
  width: 300px;
`;

export const AppFlowContinueButton: React.FC<{
  text: string;
  enabled: boolean;
  onClick: () => void;
}> = ({ text, enabled, onClick }) => {
  return (
    <ContinueButton type="button" disabled={!enabled} onClick={() => onClick()}>
      {text}
    </ContinueButton>
  );
};

export const AppFlowModal: React.FC<{
  title: string;
  body: string | React.ReactNode;
  closeButtonText: string;
  shouldShowModal: boolean;
  closeModal: () => void;
  submitButtonText?: string;
  onClickSubmit?: () => void;
  isSubmitEnabled?: boolean;
}> = ({
  title,
  body,
  closeButtonText,
  shouldShowModal,
  closeModal,
  submitButtonText,
  onClickSubmit,
  isSubmitEnabled = true,
}) => {
  return (
    <Modal show={shouldShowModal} onHide={closeModal}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{body}</Modal.Body>
      <Modal.Footer>
        <Button
          variant="link"
          className="text-decoration-none"
          onClick={closeModal}
        >
          {closeButtonText}
        </Button>
        {submitButtonText && onClickSubmit && (
          <Button
            variant="primary"
            onClick={onClickSubmit}
            disabled={!isSubmitEnabled}
          >
            {submitButtonText}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
